import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { TruckIcon, ArrowsClockwiseIcon, CheckCircleIcon, WarningCircleIcon, LinkSimpleIcon, MapPinIcon, EnvelopeIcon } from "@phosphor-icons/react";

export default function Integrations() {
  const [s, setS] = useState(null);
  const [motiveToken, setMotiveToken] = useState("");
  const [googleKey, setGoogleKey] = useState("");
  const [autoPoll, setAutoPoll] = useState(true);
  const [odoUnit, setOdoUnit] = useState("miles");
  const [acctEmail, setAcctEmail] = useState("");
  const [alertsEmail, setAlertsEmail] = useState("");
  const [alertTime, setAlertTime] = useState("21:00");
  const [alertTz, setAlertTz] = useState("America/Chicago");
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations/settings");
    setS(data);
    setAutoPoll(data.auto_poll_enabled);
    setOdoUnit(data.motive_odometer_unit || "miles");
    setAcctEmail(data.accounting_email || "");
    setAlertsEmail(data.alerts_email || "");
    setAlertTime(`${String(data.alert_hour ?? 21).padStart(2, "0")}:${String(data.alert_minute ?? 0).padStart(2, "0")}`);
    setAlertTz(data.alert_timezone || "America/Chicago");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/integrations/settings", {
        motive_api_token: motiveToken || undefined,
        google_maps_api_key: googleKey || undefined,
        auto_poll_enabled: autoPoll,
        motive_odometer_unit: odoUnit,
        accounting_email: acctEmail,
        alerts_email: alertsEmail,
        alert_hour: Number((alertTime || "21:00").split(":")[0]),
        alert_minute: Number((alertTime || "21:00").split(":")[1]),
        alert_timezone: alertTz,
      });
      setMotiveToken("");
      setGoogleKey("");
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const clear = async (key) => {
    if (!window.confirm("Clear this token?")) return;
    await api.delete(`/integrations/settings/${key}`);
    toast.success("Cleared");
    await load();
  };

  const testMotive = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/motive/test");
      toast.success(`Motive connected. Fetched ${data.sample_count} vehicle(s).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Motive test failed");
    } finally {
      setTesting(false);
    }
  };

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/integrations/motive/refresh-locations");
      if (data.ok) toast.success(`Updated ${data.updated} truck location(s)`);
      else toast.error(`Refresh failed: ${data.reason}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (!s) return <div className="p-12 font-mono text-sm text-slate-500">LOADING...</div>;

  return (
    <div data-testid="integrations-page">
      <PageHeader eyebrow="Connect" title="Integrations" subtitle="Wire up live telematics from Motive.">
        <Button onClick={refreshNow} disabled={refreshing || !s.motive_api_token_set} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="refresh-now-button">
          <ArrowsClockwiseIcon size={16} weight="bold" className="mr-2" />
          {refreshing ? "Refreshing..." : "Refresh locations now"}
        </Button>
      </PageHeader>

      <div className="p-8 space-y-6 max-w-3xl">
        {/* Motive */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="motive-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
                <TruckIcon size={20} weight="bold" className="text-white" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold tracking-tight">Motive</h3>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Fleet telematics · vehicle locations</div>
              </div>
            </div>
            {s.motive_api_token_set ? (
              <Badge tone="success" testid="motive-status"><CheckCircleIcon size={12} weight="fill" /> Connected</Badge>
            ) : (
              <Badge tone="warning" testid="motive-status"><WarningCircleIcon size={12} weight="fill" /> Not configured</Badge>
            )}
          </div>

          <div className="p-5 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">API Token</Label>
              {s.motive_api_token_set && (
                <div className="mt-1 text-xs font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1">
                  Current: {s.motive_api_token_masked}
                </div>
              )}
              <Input
                type="password"
                placeholder={s.motive_api_token_set ? "Replace with a new token..." : "Paste your Motive API token"}
                value={motiveToken}
                onChange={(e) => setMotiveToken(e.target.value)}
                className="mt-2 rounded-sm font-mono"
                data-testid="motive-token-input"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                Get from Motive Dashboard → Settings → Integrations → API Tokens. Required scopes: <span className="font-mono">vehicles.read</span>, <span className="font-mono">locations.vehicle_locations_list</span>.
              </p>
            </div>

            <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2 bg-slate-50">
              <div>
                <div className="text-sm font-semibold">Auto-poll locations every 5 minutes</div>
                <div className="text-xs text-slate-500">Recommended for live tracking</div>
              </div>
              <Switch checked={autoPoll} onCheckedChange={setAutoPoll} data-testid="auto-poll-switch" />
            </div>

            <div className="border border-slate-200 rounded-sm px-3 py-2 bg-slate-50" data-testid="motive-odo-unit-row">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Motive odometer unit</div>
                  <div className="text-xs text-slate-500">
                    Motive returns odometer in this unit — we convert to miles for storage. Flip to <span className="font-mono">kilometers</span> if Rep-Fleet shows ~1.6× the miles on the Motive dashboard.
                  </div>
                </div>
                <div className="flex items-center rounded-sm border border-slate-300 overflow-hidden bg-white shrink-0 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => setOdoUnit("miles")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${odoUnit === "miles" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                    data-testid="motive-odo-unit-miles"
                  >
                    Miles
                  </button>
                  <button
                    type="button"
                    onClick={() => setOdoUnit("kilometers")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-slate-300 ${odoUnit === "kilometers" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                    data-testid="motive-odo-unit-km"
                  >
                    Kilometers
                  </button>
                </div>
              </div>
            </div>

            {s.last_poll_at && (
              <div className="text-xs font-mono text-slate-600 border border-slate-200 rounded-sm px-3 py-2">
                <div className="flex items-center justify-between">
                  <span>Last poll: <span className={s.last_poll_status === "ok" ? "text-green-700" : "text-red-700"}>{s.last_poll_status}</span></span>
                  <span>{new Date(s.last_poll_at).toLocaleString()}</span>
                </div>
                {s.last_poll_error && <div className="text-red-700 mt-1">{s.last_poll_error}</div>}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={testMotive} disabled={testing || (!s.motive_api_token_set && !motiveToken)} variant="outline" className="rounded-sm" data-testid="test-motive-button">
                <LinkSimpleIcon size={14} weight="bold" className="mr-1.5" /> {testing ? "Testing..." : "Test connection"}
              </Button>
              {s.motive_api_token_set && (
                <Button onClick={() => clear("motive")} variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="clear-motive-button">
                  Clear token
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Google Maps */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="google-maps-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-sm flex items-center justify-center">
                <MapPinIcon size={20} weight="bold" className="text-white" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold tracking-tight">Google Maps</h3>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Address lookup · nearby services · city names</div>
              </div>
            </div>
            {s.google_maps_api_key_set ? (
              <Badge tone="success" testid="google-status"><CheckCircleIcon size={12} weight="fill" /> Connected</Badge>
            ) : (
              <Badge tone="warning" testid="google-status"><WarningCircleIcon size={12} weight="fill" /> Not configured</Badge>
            )}
          </div>

          <div className="p-5 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">API Key</Label>
              {s.google_maps_api_key_set && (
                <div className="mt-1 text-xs font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1">
                  Current: {s.google_maps_api_key_masked}
                </div>
              )}
              <Input
                type="password"
                placeholder={s.google_maps_api_key_set ? "Replace with a new key..." : "Paste your Google Maps API key"}
                value={googleKey}
                onChange={(e) => setGoogleKey(e.target.value)}
                className="mt-2 rounded-sm font-mono"
                data-testid="google-key-input"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                Get from Google Cloud Console → APIs & Services → Credentials. Enable the <span className="font-mono">Geocoding API</span> and <span className="font-mono">Places API</span>. Powers address search when holding a truck, resolves city names server-side, and "Find nearby service".
              </p>
            </div>
            {s.google_maps_api_key_set && (
              <Button onClick={() => clear("google")} variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="clear-google-button">
                Clear key
              </Button>
            )}
          </div>
        </div>

        {/* Accounting email — auto-email parts invoices */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="accounting-email-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-sm flex items-center justify-center">
              <EnvelopeIcon size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">Invoice email</h3>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Auto-email parts invoices to accounting</div>
            </div>
          </div>
          <div className="p-5 space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Send parts invoices to</Label>
            <Input
              type="text"
              placeholder="accounting@yourcompany.com, ap@yourcompany.com"
              value={acctEmail}
              onChange={(e) => setAcctEmail(e.target.value)}
              className="rounded-sm"
              data-testid="accounting-email-input"
            />
            <p className="text-[11px] text-slate-500">
              When a mechanic saves a parts invoice, Rep Fleet automatically emails it here with the unit number(s), shop name, and total — with the original invoice attached. Separate multiple addresses with commas. Leave blank to turn off.
            </p>
          </div>
        </div>

        {/* Alerts email — missed fuel-sheet upload alert */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="alerts-email-card">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <WarningCircleIcon size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">Alerts email</h3>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Missed fuel-sheet upload alert</div>
            </div>
          </div>
          <div className="p-5 space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Send alerts to</Label>
            <Input
              type="text"
              placeholder="ops@yourcompany.com, you@yourcompany.com"
              value={alertsEmail}
              onChange={(e) => setAlertsEmail(e.target.value)}
              className="rounded-sm"
              data-testid="alerts-email-input"
            />
            <p className="text-[11px] text-slate-500">
              If no fuel rate sheet has been uploaded or forwarded for a day, Rep Fleet emails this address a reminder at the time below. Separate multiple addresses with commas. Leave blank to turn off.
            </p>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Send at</Label>
                <Input
                  type="time"
                  value={alertTime}
                  onChange={(e) => setAlertTime(e.target.value)}
                  className="rounded-sm mt-1"
                  data-testid="alert-time-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Timezone</Label>
                <select
                  value={alertTz}
                  onChange={(e) => setAlertTz(e.target.value)}
                  className="w-full h-10 mt-1 px-3 rounded-sm border border-slate-300 bg-white text-sm"
                  data-testid="alert-timezone-select"
                >
                  <option value="America/New_York">Eastern (New York)</option>
                  <option value="America/Chicago">Central (Chicago)</option>
                  <option value="America/Denver">Mountain (Denver)</option>
                  <option value="America/Phoenix">Arizona (Phoenix)</option>
                  <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                  <option value="America/Anchorage">Alaska (Anchorage)</option>
                  <option value="Pacific/Honolulu">Hawaii (Honolulu)</option>
                  <option value="America/Toronto">Eastern — Canada (Toronto)</option>
                  <option value="America/Winnipeg">Central — Canada (Winnipeg)</option>
                  <option value="America/Edmonton">Mountain — Canada (Edmonton)</option>
                  <option value="America/Vancouver">Pacific — Canada (Vancouver)</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Checked every 15 minutes — the alert fires at the first check at or after your chosen time.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm h-10 px-6" data-testid="save-settings-button">
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
